import React, { useState, useRef } from 'react';
import { Dropdown, Checkbox, Radio, Bubble, SearchBox } from 'tea-component';
import NoData from './components/NoData';
import { ALL_VALUE } from './constants';
import useIndex from './hooks/useIndex';
import useSearch from './hooks/useSearch';
import { SelectorMultipleProps } from './types';
import { useLocal } from '@/pages/dashboard/utils/customHook';

import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import '@/pages/home/assets/scss/components/selectComponents.scss';

const SelectorMultiple = (props: SelectorMultipleProps) => {
  const {
    label,
    buttonLabel = label,
    className = '',
    allDescribe = '',
    allInfo = '',
    showTips = false,
    tipsInfo = '',
    disabled = false,
    positionFixed = false,
    style,
    iconTip,
    matchButtonWidth,
    isNeedHeaderTitle = true,
  } = props;
  const bindClassName = useRef(
    `selector${Math.round(Math.random() * 10000 * Date.now()).toString(16)}`,
  );
  const {
    value,
    isOpen,
    options,
    checkedAll,
    handleAll,
    handleOpen,
    handleClose,
    handleChange,
    handleRadioChange,
  } = useIndex(props);
  const { getLocalText } = useLocal('selector_multiple_component');
  const [isHover, setIsHover] = useState(false);
  const { searchValue, replaceMatch, buildHighLightData, handleSearchValueChange } =
    useSearch(options);
  const renderOptions = buildHighLightData() ?? [];
  // all 的查询匹配字符串
  const allMatchText = replaceMatch(allDescribe, searchValue);
  const isMatchData = renderOptions?.length > 0 && searchValue?.length > 0;
  const isMatchAll = allMatchText !== allDescribe;
  // 是否搜索为空
  const isMatch = isMatchAll || isMatchData;
  // 是否为清除按钮
  const isClear = renderOptions?.some((item) => value?.includes(item?.value));

  const button: React.ReactNode = (() => {
    const tags: any[] = [];

    options?.forEach((option) => {
      if (value?.includes(option.value)) {
        tags.push(option);
      }
    });

    const renderSelected = (tags: any[]) => {
      if (tags?.length > 2) return tags?.length;

      return tags?.map((item, index) => {
        // 获取名称，过滤html代码
        const text = item?.text?.replace?.(/<[^>]+>/gim, '');
        // 统一有图标则展示到选择框内
        return (
          <span key={index}>
            {item?.icon && <img src={item.icon} />}
            {/* {text} */}
            {item?.render ? item.render : text}
            {index !== tags.length - 1 && ','}
          </span>
        );
      });
    };
    return (
      <div className={`selector-multiple__header select-button`}>
        <div className="dbi-select-button-label">
          <span className="select-button-text dbd-select-button-text">
            {buttonLabel ? buttonLabel : ''}&nbsp;
          </span>
          <span>{iconTip && iconTip}</span>
        </div>
        <span className="dbd-select-button-value">
          {checkedAll ? (
            allDescribe
          ) : (
            // 和后端罗超核对后，后端可能会特意传空，所以这里不能用allDescribe,只能给空
            <>{tags.length ? renderSelected(tags) : ''}</>
          )}
        </span>
      </div>
    );
  })();

  return (
    <Bubble
      dark
      escapeWithReference
      flipped={false}
      destroyOnClose={false}
      content={showTips && !isOpen ? tipsInfo : null}
    >
      <Dropdown
        clickClose={false}
        matchButtonWidth={matchButtonWidth}
        className={`select-intelligence-box ${bindClassName.current} ${className}`}
        style={{ marginRight: 10, ...(style || {}) }}
        button={button}
        appearance="button"
        onOpen={() => {
          // 清空操作放在close中会导致结果闪动的问题，所以放在open方法中处理
          handleSearchValueChange('');
          handleOpen();
        }}
        onClose={handleClose}
        // closeOnScroll={false}
        positionFixed={positionFixed}
        popupContainer={() => document.querySelector(`.${bindClassName.current}`) || document.body}
        boxClassName="selector-multiple__dropdown"
        overlayClassName={'selector-multiple__dropdown-overlay'}
        disabled={disabled}
      >
        <SearchBox
          placeholder={getLocalText('placeholder')}
          className="selector-multiple-search-input"
          onChange={handleSearchValueChange}
          value={searchValue}
        />
        <div className="selector-multiple__dropdown-inner">
          <div className="select-context">
            {isMatchAll && (
              <>
                {isNeedHeaderTitle && (
                  <div className="select-header-title">
                    <span className="label">{getLocalText('overall')}</span>
                  </div>
                )}
                <div className="select-header-felx">
                  <Radio.Group onChange={handleRadioChange} value={checkedAll ? ALL_VALUE : ''}>
                    <Radio name={ALL_VALUE}>
                      <span dangerouslySetInnerHTML={{ __html: `${allMatchText}` }} />
                      {allInfo && (
                        <div
                          className="select-header-icon-box"
                          onMouseOver={() => {
                            setIsHover(true);
                          }}
                          onMouseOut={() => {
                            setIsHover(false);
                          }}
                        >
                          <Bubble
                            trigger="hover"
                            arrowPointAtCenter
                            placement="top-start"
                            content={allInfo}
                            dark
                          >
                            {/* 此处必须包一层，不然点击不生效 */}
                            <i>
                              <SvgIcon
                                className="select-header-ic"
                                type={isHover ? 'alert-circle-action' : 'alert-circle-m'}
                              />
                            </i>
                          </Bubble>
                        </div>
                      )}
                    </Radio>
                  </Radio.Group>
                </div>
              </>
            )}
            <>
              {renderOptions?.length > 0 && (
                <div className="select-felx">
                  <span className="select-felx-lf">{label}</span>
                  <span
                    onClick={() => handleAll(renderOptions, isClear)}
                    className="select-felx-rt"
                  >
                    {isClear ? getLocalText('clear_all') : getLocalText('select_all')}
                  </span>
                </div>
              )}
            </>
          </div>
          {(isMatch && (
            <div className="list-container">
              <div className="tea-form-checks">
                <Checkbox.Group
                  layout="column"
                  value={value}
                  className="list"
                  onChange={handleChange}
                >
                  {renderOptions?.map((item) => (
                    <Checkbox key={item.value} name={item.value}>
                      {item.icon && <img src={item.icon} />}
                      {item.render && !searchValue ? (
                        item.render
                      ) : (
                        <span
                          className={item.icon ? 'dbd-tea-form-checks-span-has-icon' : ''}
                          dangerouslySetInnerHTML={{ __html: `${item.text}` }}
                        />
                      )}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </div>
          )) || <NoData />}
        </div>
      </Dropdown>
    </Bubble>
  );
};

export default SelectorMultiple;
